import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Sparkles, BookOpenCheck, GitCompare, Lightbulb } from "lucide-react";
import { api } from "../lib/api";
import { numberToHebrew, bookHe as bookHeOf } from "../lib/hebrewRef";
import { useLanguage } from "../context/LanguageContext";
import { requireAuth } from "../lib/authGate";
import { toast } from "sonner";
import SaveToNotebookButton from "./SaveToNotebookButton";
import Markdown from "./Markdown";

const MODE_KEYS = [
  { key: "explain",  icon: BookOpenCheck, i18n: "ai_assistant.mode_explain" },
  { key: "rashi",    icon: Lightbulb,     i18n: "ai_assistant.mode_rashi" },
  { key: "compare",  icon: GitCompare,    i18n: "ai_assistant.mode_compare" },
  { key: "insight",  icon: Sparkles,      i18n: "ai_assistant.mode_insight" },
];

// Internal provider — fixed to Claude/Anthropic. Hidden from the user
// per product decision: "פירוש חכם" is the brand, the underlying model
// is an implementation detail.
const INTERNAL_PROVIDER = "anthropic";

export default function AIAssistant({ book, chapter, verse, enabled }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  const { lang } = useLanguage();

  const run = async (mode) => {
    // Anonymous users see the auth modal instead of a toast — same UX
    // as every other gated feature in the app.
    if (!requireAuth("ai")) return;
    if (!enabled) {
      toast.error(t("ai_assistant.login_required"));
      return;
    }
    setLoading(true);
    setActiveMode(mode);
    setResult(null);
    try {
      const res = await api.aiExplain({
        book,
        chapter,
        verse,
        mode,
        provider: INTERNAL_PROVIDER,
        lang,
      });
      setResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ai_assistant.request_failed"));
    } finally {
      setLoading(false);
    }
  };

  const modeLabel = (key) => t(MODE_KEYS.find((m) => m.key === key)?.i18n || "");

  return (
    <div className="space-y-4" data-testid="ai-assistant" dir={isEn ? "ltr" : "rtl"}>
      <p className="text-sm text-muted-foreground">
        {t("ai_assistant.preface")}
      </p>

      <div className="grid grid-cols-2 gap-2">
        {MODE_KEYS.map((m) => {
          const Icon = m.icon;
          return (
            <Button
              key={m.key}
              data-testid={`ai-mode-${m.key}`}
              onClick={() => run(m.key)}
              disabled={loading || !enabled}
              className={`sacred-btn-ghost !py-3 ${isEn ? "text-left" : "text-right"} justify-start`}
            >
              <Icon className="h-4 w-4 ms-2" strokeWidth={1.5} />
              <span>{t(m.i18n)}</span>
            </Button>
          );
        })}
      </div>

      {!enabled && (
        <p className="text-xs text-muted-foreground text-center py-3 border border-border/40 rounded-sm">
          {t("ai_assistant.must_login")}
        </p>
      )}

      {loading && (
        <div className="space-y-2" data-testid="ai-loading">
          <div className="h-4 shimmer-bg rounded w-5/6" />
          <div className="h-4 shimmer-bg rounded w-full" />
          <div className="h-4 shimmer-bg rounded w-3/4" />
          <p className="text-center text-xs text-muted-foreground animate-pulse">
            {t("ai_assistant.thinking")}
          </p>
        </div>
      )}

      {result && !loading && (
        <div
          data-testid="ai-result"
          className="rounded-sm border border-gold/30 bg-card/50 p-5 space-y-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gold">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-heading text-sm tracking-wide uppercase">
                {t("ai_assistant.result_eyebrow")}
              </span>
            </div>
          </div>
          <div className="gold-rule" />
          {/* The AI body itself is generated in Hebrew (Phase 2 will add
              an English-mode prompt). Lock the rendering direction to RTL
              so Hebrew letters/nikud render correctly even when the page
              shell is in English. */}
          <Markdown className="text-right">{result.text}</Markdown>
          {result.sources?.length > 0 && (
            <p className="text-xs text-muted-foreground pt-2 border-t border-border/40">
              {t("ai_assistant.based_on_sources", { sources: result.sources.join(" · ") })}
            </p>
          )}
          <p className="text-[10px] text-muted-foreground/80">
            {t("ai_assistant.disclaimer")}
          </p>
          <div className="flex justify-end pt-2 border-t border-border/40">
            <SaveToNotebookButton
              variant="chip"
              testIdSuffix="ai"
              payload={{
                source_type: "ai",
                source_book: book,
                source_chapter: chapter,
                source_verse: verse,
                source_label: `${t("ai_assistant.result_eyebrow")} · ${activeMode ? modeLabel(activeMode) : ""}`,
                source_ref_he: book && chapter && verse ? `${bookHeOf(book) || book} ${numberToHebrew(chapter)}:${numberToHebrew(verse)}` : undefined,
                title: activeMode ? modeLabel(activeMode) : t("ai_assistant.save_title_default"),
                content_text: result.text,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
